import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Building2, Search, Check, ArrowLeft } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';

const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'worker',
    companyCode: '',
    craft: '',
    divisionId: ''
  });
  const [crafts, setCrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [loadingDivisions, setLoadingDivisions] = useState(false);

  useEffect(() => {
    const loadCrafts = async () => {
      try {
        const response = await axiosInstance.get('/public/crafts');
        setCrafts(response.data);
      } catch (error) {
        console.error('Failed to load crafts:', error);
      }
    };
    loadCrafts();
  }, []);

  useEffect(() => {
    const searchCompanies = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const response = await axiosInstance.get(`/companies/search?query=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };
    const debounceTimer = setTimeout(searchCompanies, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setRegisterData({ ...registerData, companyCode: company.companyCode || '', divisionId: '' });
    setSearchQuery('');
    setShowSearchResults(false);
    setDivisions([]);
    toast.success(`Selected: ${company.name}`);
  };

  useEffect(() => {
    const fetchDivisions = async () => {
      if (!selectedCompany?.id) {
        setDivisions([]);
        return;
      }
      setLoadingDivisions(true);
      try {
        const response = await axiosInstance.get(`/public/companies/${selectedCompany.id}/divisions`);
        setDivisions(response.data);
        if (response.data.length > 0) {
          toast.info(`${response.data.length} division(s) available for this company`);
        }
      } catch (error) {
        setDivisions([]);
      } finally {
        setLoadingDivisions(false);
      }
    };
    fetchDivisions();
  }, [selectedCompany]);

  useEffect(() => {
    const fetchCompanyByCode = async () => {
      const code = registerData.companyCode?.trim();
      if (!code || selectedCompany?.companyCode === code) return;
      const timer = setTimeout(async () => {
        setLoadingDivisions(true);
        try {
          const companyResponse = await axiosInstance.get(`/public/companies/by-code/${encodeURIComponent(code)}`);
          if (companyResponse.data) {
            const divisionsResponse = await axiosInstance.get(`/public/companies/${companyResponse.data.id}/divisions`);
            setDivisions(divisionsResponse.data);
            if (divisionsResponse.data.length > 0) {
              toast.info(`${divisionsResponse.data.length} division(s) available for ${companyResponse.data.name}`);
            }
          }
        } catch (error) {
          setDivisions([]);
        } finally {
          setLoadingDivisions(false);
        }
      }, 800);
      return () => clearTimeout(timer);
    };
    fetchCompanyByCode();
  }, [registerData.companyCode, selectedCompany]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.role === 'worker' && !registerData.craft) {
      toast.error('Please select your craft/trade');
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/register', registerData);
      const userData = response.data.user;
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success('Account created successfully!');
      const role = userData.role;
      if (role === 'worker') {
        window.location.href = '/dashboard';
      } else if (role === 'supervisor') {
        window.location.href = '/supervisor';
      } else if (role === 'super_admin') {
        window.location.href = '/superadmin';
      } else {
        window.location.href = '/admin';
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4 pb-32 sm:pb-24">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={() => navigate('/login')}
          data-testid="register-back-to-login"
          className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </button>
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Create your account</h1>
            <p className="text-sm text-slate-500 mt-1">Join your company on SafetyMind AI</p>
          </div>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="register-name">{t('auth.fullName')}</Label>
              <Input
                id="register-name"
                data-testid="register-name-input"
                type="text"
                placeholder="John Doe"
                value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email">{t('common.email')}</Label>
              <Input
                id="register-email"
                data-testid="register-email-input"
                type="email"
                placeholder="your@email.com"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password">Password</Label>
              <Input
                id="register-password"
                data-testid="register-password-input"
                type="password"
                placeholder="••••••••"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-role">Role</Label>
              <Select
                value={registerData.role}
                onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
              >
                <SelectTrigger id="register-role" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="worker" data-testid="role-worker">{t('auth.roleWorker')}</SelectItem>
                  <SelectItem value="service_worker" data-testid="role-service-worker">{t('auth.roleServiceWorker')}</SelectItem>
                  <SelectItem value="supervisor" data-testid="role-supervisor">{t('auth.roleSupervisor')}</SelectItem>
                  <SelectItem value="admin" data-testid="role-admin">{t('auth.roleAdmin')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {registerData.role === 'worker' && (
              <div className="space-y-2">
                <Label htmlFor="register-craft">{t('auth.craftTrade')} *</Label>
                <Select
                  value={registerData.craft}
                  onValueChange={(value) => setRegisterData({ ...registerData, craft: value })}
                  required
                >
                  <SelectTrigger id="register-craft" className="w-full">
                    <SelectValue placeholder="Select your craft/trade" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {crafts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-500">Loading crafts...</div>
                    ) : (
                      crafts
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((craft) => (
                          <SelectItem key={craft.id} value={craft.name}>
                            {craft.name}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
                {registerData.craft && crafts.find(c => c.name === registerData.craft)?.description && (
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-medium">Description:</span> {crafts.find(c => c.name === registerData.craft)?.description}
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  Your training courses will be customized based on your craft
                </p>
              </div>
            )}

            {divisions.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="register-division">Division (Optional)</Label>
                <Select
                  value={registerData.divisionId}
                  onValueChange={(value) => setRegisterData({ ...registerData, divisionId: value === 'no-division' ? '' : value })}
                >
                  <SelectTrigger id="register-division" data-testid="register-division-select" disabled={loadingDivisions}>
                    <SelectValue placeholder={loadingDivisions ? "Loading divisions..." : "Select a division (optional)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-division">No Division</SelectItem>
                    {divisions.map((division) => (
                      <SelectItem key={division.id} value={division.id}>
                        {division.name}
                        {division.description && (
                          <span className="text-xs text-slate-500 ml-2">- {division.description}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Select your division if applicable. You can be assigned to one later.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="register-company-code">{t('auth.companyCode')} *</Label>
              <Popover open={showSearchResults} onOpenChange={setShowSearchResults}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    type="button"
                    className="w-full justify-between text-left font-normal"
                    onClick={() => setShowSearchResults(true)}
                  >
                    {selectedCompany ? (
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {selectedCompany.name}
                        <span className="text-xs text-slate-500">({selectedCompany.companyCode})</span>
                      </span>
                    ) : (
                      <span className="text-slate-500">Search for your company...</span>
                    )}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by company name or code..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {searching ? 'Searching...' : searchQuery.length < 2 ? 'Type at least 2 characters...' : 'No companies found.'}
                      </CommandEmpty>
                      {searchResults.length > 0 && (
                        <CommandGroup>
                          {searchResults.map((company) => (
                            <CommandItem
                              key={company.id}
                              value={company.name}
                              onSelect={() => handleSelectCompany(company)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center gap-2 w-full">
                                <Building2 className="w-4 h-4 text-orange-500" />
                                <div className="flex-1">
                                  <div className="font-medium">{company.name}</div>
                                  <div className="text-xs text-slate-500">Code: {company.companyCode || 'No code'}</div>
                                </div>
                                {selectedCompany?.id === company.id && (
                                  <Check className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <div className="relative">
                <Label htmlFor="manual-company-code" className="text-xs text-slate-600">
                  Or enter code manually:
                </Label>
                <Input
                  id="manual-company-code"
                  data-testid="register-company-code-input"
                  type="text"
                  placeholder="e.g., ABC123"
                  value={registerData.companyCode}
                  onChange={(e) => {
                    setRegisterData({ ...registerData, companyCode: e.target.value.toUpperCase() });
                    setSelectedCompany(null);
                  }}
                  required
                  className="mt-1"
                />
              </div>

              <p className="text-xs text-slate-500">
                {selectedCompany ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Company selected: {selectedCompany.name}
                  </span>
                ) : (
                  'Search for your company or enter the code provided by your employer'
                )}
              </p>
            </div>

            <Button
              type="submit"
              data-testid="register-submit-button"
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-slate-600 mb-2">Don't have a company code?</p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-orange-200 hover:bg-orange-50"
                onClick={() => navigate('/company-register')}
                data-testid="register-your-company-btn"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Register Your Company
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
